// CatalogStudioRecipeCard (features/admin/CatalogStudioRecipeCard.tsx) — EN + TR.
//
// Found hardcoded Turkish-only during the 2026-09-28 language-purity audit
// (docs/audits/2026-09-28-dil-saflik-denetimi.md) — this card had no locale
// wiring at all. A few strings are thrown/used outside React render
// (mutationFn / query helpers), so callers read them via
// `pickT(catalogStudioRecipeI18n, useLocaleStore.getState().locale)` rather
// than the `useT` hook (see SupplyPurchaseOrderInbox.tsx for the same
// pattern).

import type { LocalizedDict } from '../index'

type CatalogStudioRecipeStrings = {
  header: {
    title: string
    subtitle: string
    draftToPilotBadge: string
  }
  infoBanner: string
  previewModeBanner: string
  loadingRecipes: string
  loadError: string
  unknownOutcomeBanner: string
  retrySameRequest: string
  pilotDialog: {
    title: string
    description: (name: string, version: number) => string
    note: string
    cancel: string
    confirm: string
  }
  repairDialog: {
    title: string
    description: (name: string, version: number) => string
    promptLabel: string
    noSpendNote: string
    unknownOutcome: string
    cancel: string
    confirm: string
    retry: string
  }
  recipeList: {
    blockedNote: string
    specSummary: (resolution: string, aspectRatio: string) => string
    makePilot: string
    showPrompt: string
    compatible: string
    incompatible: string
    compatibilityEvidence: (actualChars: number, maxChars: number) => string
    createCompatibleReplacement: string
    replacedHistory: string
  }
  statusLabels: { draft: string; pilot: string; active: string; retired: string }
  draftForm: {
    recipeNameLabel: string
    methodLabel: string
    resolutionLabel: string
    aspectRatioLabel: string
    aspectRatioValue: string
    promptLabel: string
    creating: string
    createDraft: string
  }
  toasts: {
    createSuccessTitle: string
    createSuccessDescription: string
    createErrorTitle: string
    pilotSuccessTitle: string
    pilotSuccessDescription: string
    pilotErrorTitle: string
    repairSuccessTitle: string
    repairSuccessDescription: string
    repairErrorTitle: string
  }
  errors: {
    genericRequestFailed: string
    recipesLoadFailed: string
    sessionStorageRequired: string
    pilotSubmittedAfterUnmount: string
    pilotAlreadyManaged: string
  }
}

export const catalogStudioRecipeI18n: LocalizedDict<CatalogStudioRecipeStrings> = {
  en: {
    header: {
      title: 'Catalog Studio recipe',
      subtitle: 'Manage the Magnific instruction that preserves jewelry identity and the source image’s presentation type.',
      draftToPilotBadge: 'Draft → Pilot',
    },
    infoBanner:
      'Recipe management does not generate images and does not spend Magnific credit. Pilot status only makes this instruction selectable in the catalog preflight.',
    previewModeBanner: 'Preview mode — recipe management can only be saved against a real API connection.',
    loadingRecipes: 'Loading recipes…',
    loadError: 'Could not load the recipe list. Refresh the page and try again.',
    unknownOutcomeBanner: 'The outcome of this first save is unknown. Retry the same request instead of opening a new draft.',
    retrySameRequest: 'Retry the same request',
    pilotDialog: {
      title: 'Make this recipe the pilot?',
      description: (name, version) => `${name} v${version} will become selectable in the catalog preflight.`,
      note: 'This action does not generate images and does not spend credit.',
      cancel: 'Cancel',
      confirm: 'Confirm pilot',
    },
    repairDialog: {
      title: 'Create a compatible replacement?',
      description: (name, version) => `Review the prompt that will replace ${name} v${version} as the active immutable version.`,
      promptLabel: 'Replacement prompt',
      noSpendNote: 'This repair does not generate an image or spend Magnific credit.',
      unknownOutcome: 'The repair result is unknown. Retry the same request so a second version cannot be created.',
      cancel: 'Cancel',
      confirm: 'Confirm replacement',
      retry: 'Retry the same repair',
    },
    recipeList: {
      blockedNote: 'Another pilot or active recipe is already managing auto-selection. A second pilot can’t be opened from this screen.',
      specSummary: (resolution, aspectRatio) => `Seedream 5 Pro Edit · ${resolution} · ${aspectRatio}`,
      makePilot: 'Make pilot',
      showPrompt: 'Show prompt',
      compatible: 'Compatible',
      incompatible: 'Incompatible',
      compatibilityEvidence: (actualChars, maxChars) =>
        `This recipe is not compatible with Magnific: the prompt is ${actualChars.toLocaleString('en-US')} characters; Seedream 5 Pro allows ${maxChars.toLocaleString('en-US')}. Create a compatible replacement. This action does not generate an image or spend credit.`,
      createCompatibleReplacement: 'Create compatible replacement',
      replacedHistory: 'Replaced by a compatible active version',
    },
    statusLabels: { draft: 'Draft', pilot: 'Pilot', active: 'Active', retired: 'Retired' },
    draftForm: {
      recipeNameLabel: 'Recipe name',
      methodLabel: 'Method',
      resolutionLabel: 'Resolution',
      aspectRatioLabel: 'Aspect ratio',
      aspectRatioValue: 'Square 1:1',
      promptLabel: 'Prompt',
      creating: 'Creating…',
      createDraft: 'Create draft',
    },
    toasts: {
      createSuccessTitle: 'Recipe draft created',
      createSuccessDescription: 'Not a pilot yet, and no Magnific credit was spent.',
      createErrorTitle: 'Could not create recipe draft',
      pilotSuccessTitle: 'Recipe made pilot',
      pilotSuccessDescription: 'The catalog preflight can now select this recipe. No image has been generated yet.',
      pilotErrorTitle: 'Could not make recipe pilot',
      repairSuccessTitle: 'Compatible replacement activated',
      repairSuccessDescription: 'The old recipe remains unchanged. No image was generated and no Magnific credit was spent.',
      repairErrorTitle: 'Could not replace incompatible recipe',
    },
    errors: {
      genericRequestFailed: 'The request could not be completed.',
      recipesLoadFailed: 'Could not load the recipe list.',
      sessionStorageRequired: 'Browser session storage is required for a safe save.',
      pilotSubmittedAfterUnmount: 'The pilot action was not submitted because the page closed.',
      pilotAlreadyManaged: 'Another pilot or active recipe is now managing selection. Refresh the page.',
    },
  },
  tr: {
    header: {
      title: 'Catalog Studio tarifi',
      subtitle: 'Takı kimliğini ve kaynak görselin sunum türünü koruyan Magnific talimatını yönetin.',
      draftToPilotBadge: 'Taslak → Pilot',
    },
    infoBanner:
      'Recipe yönetimi görsel üretmez ve Magnific kredisi harcamaz. Pilot durumu yalnız katalog ön kontrolünde bu talimatı seçilebilir yapar.',
    previewModeBanner: 'Önizleme modu — recipe yönetimi yalnız gerçek API bağlantısında kaydedilebilir.',
    loadingRecipes: 'Recipe’ler yükleniyor…',
    loadError: 'Recipe listesi yüklenemedi. Sayfayı yenileyip tekrar deneyin.',
    unknownOutcomeBanner: 'Bu ilk kaydın sonucu belirsiz. Yeni taslak açmadan aynı isteği tekrar deneyin.',
    retrySameRequest: 'Aynı isteği tekrar dene',
    pilotDialog: {
      title: 'Recipe pilot yapılsın mı?',
      description: (name, version) => `${name} v${version} katalog ön kontrolünde seçilebilir olacak.`,
      note: 'Bu işlem görsel üretmez ve kredi harcamaz.',
      cancel: 'Vazgeç',
      confirm: 'Pilotu onayla',
    },
    repairDialog: {
      title: 'Uyumlu replacement oluşturulsun mu?',
      description: (name, version) => `${name} v${version} yerine aktif olacak değişmez sürümün promptunu inceleyin.`,
      promptLabel: 'Replacement promptu',
      noSpendNote: 'Bu onarım görsel üretmez ve Magnific kredisi harcamaz.',
      unknownOutcome: 'Onarım sonucu belirsiz. İkinci sürüm oluşmaması için aynı isteği yeniden deneyin.',
      cancel: 'Vazgeç',
      confirm: 'Replacement’ı onayla',
      retry: 'Aynı onarımı tekrar dene',
    },
    recipeList: {
      blockedNote: 'Başka bir pilot veya aktif recipe zaten otomatik seçimi yönetiyor. İkinci pilot bu ekrandan açılamaz.',
      specSummary: (resolution, aspectRatio) => `Seedream 5 Pro Edit · ${resolution} · ${aspectRatio}`,
      makePilot: 'Pilot yap',
      showPrompt: 'Promptu göster',
      compatible: 'Uyumlu',
      incompatible: 'Uyumsuz',
      compatibilityEvidence: (actualChars, maxChars) =>
        `Bu tarif Magnific ile uyumlu değil: prompt ${actualChars.toLocaleString('tr-TR')} karakter, Seedream 5 Pro sınırı ${maxChars.toLocaleString('tr-TR')}. Uyumlu yeni sürüm oluşturabilirsiniz. Bu işlem görsel üretmez ve kredi harcamaz.`,
      createCompatibleReplacement: 'Uyumlu replacement oluştur',
      replacedHistory: 'Uyumlu aktif sürümle değiştirildi',
    },
    statusLabels: { draft: 'Taslak', pilot: 'Pilot', active: 'Aktif', retired: 'Emekli' },
    draftForm: {
      recipeNameLabel: 'Recipe adı',
      methodLabel: 'Yöntem',
      resolutionLabel: 'Çözünürlük',
      aspectRatioLabel: 'En-boy oranı',
      aspectRatioValue: 'Kare 1:1',
      promptLabel: 'Prompt',
      creating: 'Oluşturuluyor…',
      createDraft: 'Taslak oluştur',
    },
    toasts: {
      createSuccessTitle: 'Recipe taslağı oluşturuldu',
      createSuccessDescription: 'Henüz pilot değildir ve Magnific kredisi harcanmadı.',
      createErrorTitle: 'Recipe taslağı oluşturulamadı',
      pilotSuccessTitle: 'Recipe pilot yapıldı',
      pilotSuccessDescription: 'Katalog ön kontrolü artık bu recipe’yi seçebilir. Henüz görsel üretilmedi.',
      pilotErrorTitle: 'Recipe pilot yapılamadı',
      repairSuccessTitle: 'Uyumlu replacement aktifleştirildi',
      repairSuccessDescription: 'Eski recipe değişmeden korundu. Görsel üretilmedi ve Magnific kredisi harcanmadı.',
      repairErrorTitle: 'Uyumsuz recipe değiştirilemedi',
    },
    errors: {
      genericRequestFailed: 'İstek tamamlanamadı.',
      recipesLoadFailed: 'Recipe listesi yüklenemedi.',
      sessionStorageRequired: 'Güvenli kayıt için tarayıcı oturum deposu gerekli.',
      pilotSubmittedAfterUnmount: 'Pilot işlemi sayfa kapandığı için gönderilmedi.',
      pilotAlreadyManaged: 'Başka bir pilot veya aktif recipe artık seçimi yönetiyor. Sayfayı yenileyin.',
    },
  },
}
